<template>
    <div class="order-collection">

        <!-- Toolbar -->
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
            <div class="d-flex align-items-center">
                <span class="text-muted mr-2" style="white-space:nowrap;">
                    {{ filteredOrders.length }} order{{ filteredOrders.length !== 1 ? 's' : '' }}
                </span>
                <b-badge v-if="searchText" variant="light" class="border ml-1" style="cursor:pointer;" @click="searchText = ''">
                    "{{ searchText }}" <font-awesome-icon icon="times" class="ml-1"/>
                </b-badge>
            </div>
            <b-input-group size="sm" style="max-width:280px;">
                <b-input-group-prepend is-text>
                    <font-awesome-icon icon="search"/>
                </b-input-group-prepend>
                <b-form-input
                    v-model="searchText"
                    placeholder="Search orders…"
                    debounce="250"
                />
                <b-input-group-append v-if="searchText">
                    <b-button variant="outline-secondary" @click="searchText = ''">
                        <font-awesome-icon icon="times"/>
                    </b-button>
                </b-input-group-append>
            </b-input-group>
        </div>

        <!-- Table -->
        <b-table
            :items="filteredOrders"
            :fields="fields"
            :per-page="disablePagination ? 0 : ordersPerPage"
            :current-page="disablePagination ? 1 : currentPage"
            hover
            responsive
            show-empty
            empty-text="No orders found."
            class="orders-table mb-2"
            tbody-tr-class="order-row"
        >
            <!-- Expand toggle -->
            <template #cell(expand)="row">
                <b-button size="sm" variant="link" class="expand-btn p-0" @click="row.toggleDetails">
                    <font-awesome-icon
                        :icon="row.detailsShowing ? 'chevron-up' : 'chevron-down'"
                        class="text-muted"
                    />
                </b-button>
            </template>

            <!-- Date -->
            <template #cell(date)="data">
                <span class="text-nowrap">{{ data.item.date | orderDate }}</span>
            </template>

            <!-- Name + PO -->
            <template #cell(shippingname)="data">
                <div class="font-weight-semibold">{{ data.item.shippingname }}</div>
                <small v-if="data.item.purchase_order" class="text-muted">PO: {{ data.item.purchase_order }}</small>
            </template>

            <!-- Ship to -->
            <template #cell(location)="data">
                <span class="text-nowrap">
                    {{ data.item.shippingcity }}, {{ data.item.shippingstate }} {{ data.item.shippingzip }}
                </span>
            </template>

            <!-- Items count -->
            <template #cell(items)="data">
                <b-badge variant="secondary" pill>{{ (data.item.items || []).length }}</b-badge>
            </template>

            <!-- Total -->
            <template #cell(total)="data">
                <strong>{{ data.item.total | currency }}</strong>
            </template>

            <!-- Shipping status -->
            <template #cell(shipping_status)="data">
                <b-badge :variant="shippingVariant(data.item.shipping_status)" class="text-capitalize">
                    {{ shippingLabel(data.item.shipping_status) }}
                </b-badge>
            </template>

            <!-- Expanded row detail -->
            <template #row-details="row">
                <div class="order-detail-panel">
                    <Order :order="row.item" />
                </div>
            </template>
        </b-table>

        <!-- Pagination -->
        <b-pagination
            v-if="!disablePagination && filteredOrders.length > ordersPerPage"
            v-model="currentPage"
            :total-rows="filteredOrders.length"
            :per-page="ordersPerPage"
            align="right"
            size="sm"
            class="mb-0"
        />
    </div>
</template>

<script>
import OrderDetails from "@/components/dashboard/OrderDetails.vue";

export default {
    name: "OrderCollection",
    components: { Order: OrderDetails },
    props: ['orders', 'disablePagination'],

    data: () => ({
        ordersPerPage: 25,
        currentPage: 1,
        filteredOrders: [],
        searchText: "",
    }),

    computed: {
        fields() {
            return [
                { key: 'expand',          label: '',               thStyle: 'width:36px' },
                { key: 'date',            label: 'Date',           sortable: true },
                { key: 'shippingname',    label: 'Name',           sortable: true },
                { key: 'location',        label: 'Ship To',        sortable: false },
                { key: 'items',           label: 'Items',          sortable: true },
                { key: 'total',           label: 'Total',          sortable: true },
                { key: 'shipping_status', label: 'Status',         sortable: false },
            ];
        },
    },

    watch: {
        searchText() {
            this.currentPage = 1;
            this.doFilter();
        },
        orders() {
            this.doFilter();
        },
    },

    created() {
        this.doFilter();
    },

    methods: {
        doFilter() {
            let result = _.sortBy(this.orders || [], 'date').reverse();
            const q = (this.searchText || '').toLowerCase().trim();
            if (q) {
                result = result.filter(o =>
                    (o.purchase_order || '').toLowerCase().includes(q) ||
                    (o.shippingname   || '').toLowerCase().includes(q) ||
                    (o.shippingaddress|| '').toLowerCase().includes(q) ||
                    (o.shippingcity   || '').toLowerCase().includes(q) ||
                    (o.shippingstate  || '').toLowerCase().includes(q) ||
                    String(o.shippingzip || '').includes(q) ||
                    (o.email          || '').toLowerCase().includes(q)
                );
            }
            this.filteredOrders = result;
        },

        shippingLabel(status) {
            console.log(status);
            if (!status || !status.delivery_status) return 'Processing';
            return status.delivery_status;
        },

        shippingVariant(status) {
            if (!status || !status.delivery_status) return 'secondary';
            const s = status.delivery_status.toLowerCase();
            if (s.includes('delivered'))  return 'success';
            if (s.includes('transit') || s.includes('shipped')) return 'info';
            if (s.includes('exception') || s.includes('failed')) return 'danger';
            return 'warning';
        },
    },

    filters: {
        orderDate(val) {
            return new Date(parseInt(val) * 1000).toLocaleDateString(undefined, {
                year: 'numeric', month: 'short', day: 'numeric',
            });
        },
    },
};
</script>

<style scoped lang="scss">
.order-collection {
    font-size: .9rem;
}

.orders-table {
    ::v-deep thead th {
        background: #f8f9fa;
        border-bottom: 2px solid #dee2e6;
        font-size: .78rem;
        text-transform: uppercase;
        letter-spacing: .04em;
        color: #6c757d;
        white-space: nowrap;
    }

    ::v-deep .order-row {
        cursor: default;
        transition: background .1s;
        &:hover > td { background: #f0f4ff; }
    }

    ::v-deep td {
        vertical-align: middle;
        padding: .6rem .75rem;
    }
}

.expand-btn {
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background .15s;
    text-decoration: none;
    &:hover { background: #e9ecef; }
}

.font-weight-semibold { font-weight: 600; }

.order-detail-panel {
    background: #fafbff;
    border-top: 2px solid #e3e8f5;
    border-bottom: 2px solid #e3e8f5;

    ::v-deep .card-body { padding: 1.25rem 1.5rem; }
    ::v-deep .alert-warning { border-radius: .375rem; margin-top: 1rem; }
}
</style>